import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from '../../i18n/I18nContext';
import { useToast } from '../../contexts/ToastContext';
import { apiClient } from '../../utils/api-client';
import { Dialog } from '../../components/ui/dialog';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackDialog: FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.post('/reader/feedback', { text: text.trim() });
      showToast(t('feedback.thanks'), 'success');
      setText('');
      onClose();
    } catch {
      showToast(t('feedback.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const footerContent = (
    <div className="flex justify-end gap-2">
      <button
        onClick={onClose}
        disabled={submitting}
        className="px-4 py-2 text-sm rounded border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-50"
      >
        {t('common.cancel')}
      </button>
      <button
        onClick={handleSubmit}
        disabled={submitting || !text.trim()}
        className="px-4 py-2 text-sm rounded font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-colors disabled:opacity-50"
      >
        {t('feedback.submit')}
      </button>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('feedback.title')}
      maxWidth="sm"
      footer={footerContent}
      disableClose={submitting}
    >
      <div className="px-4 sm:px-6 py-4">
        <p className="text-sm text-muted-foreground mb-3">{t('feedback.prompt')}</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={5000}
          rows={5}
          className="w-full border border-input rounded px-3 py-2 bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder={t('feedback.placeholder')}
          disabled={submitting}
        />
      </div>
    </Dialog>
  );
};

export default FeedbackDialog;
